/**
 * Fixtures typed against the agreed #497 wire shape. Tests and the dev probe
 * import this; nothing else may.
 *
 * Deliberate mismatches, so a test can tell "read the figure" from "derived it":
 *  - the twelve periods' income sums to 64,200.00 while `totals.income` is
 *    64,230.00 (so the period nets sum to 11,400.00 against `totals.net` of
 *    11,430.00 — the same mismatch, carried through);
 *  - group `p-living` is 37,000.00 while its listed children (Housing,
 *    Groceries, Transport) sum to 36,120.00 — the category list is the top six
 *    only, and the group figure is the backend's, not a sum;
 *  - `merchants.remainder.amount` is 16,391.00 while expense minus the top six
 *    merchants is 14,807.00, so the remainder row must read the server's figure.
 *
 * Group semantics (the backend contract): a ROOT category is its own group
 * (Health is group `health`), and `group_id: null` means uncategorised ONLY, so
 * the null group carries exactly `categories.uncategorised.amount`. No category
 * ever has `group_id: null`.
 * Everything else is internally consistent (net = income − expense).
 *
 * The dev probe imports this, so nothing at module scope may read one fixture
 * off another: see WINDOW/TOTALS below. `probe.build.test.ts` greps a real
 * production build for this file's data to prove it is dropped.
 */
import type { CategoryInfo } from './cashflowCategories'
import type { CashflowWire } from './cashflowWire'

/** One point per period (12), as the sparkline draws it. */
const series = (point: string): string[] => Array.from({ length: 12 }, () => point)

type BucketWire = NonNullable<CashflowWire['buckets']>[number]

const MONTHS: readonly { start: string; end: string; expense: string; net: string }[] = [
  { start: '2025-08-01', end: '2025-08-31', expense: '4100.00', net: '1250.00' },
  { start: '2025-09-01', end: '2025-09-30', expense: '4150.00', net: '1200.00' },
  { start: '2025-10-01', end: '2025-10-31', expense: '4200.00', net: '1150.00' },
  { start: '2025-11-01', end: '2025-11-30', expense: '4300.00', net: '1050.00' },
  { start: '2025-12-01', end: '2025-12-31', expense: '5710.00', net: '-360.00' },
  { start: '2026-01-01', end: '2026-01-31', expense: '4050.00', net: '1300.00' },
  { start: '2026-02-01', end: '2026-02-28', expense: '3990.00', net: '1360.00' },
  { start: '2026-03-01', end: '2026-03-31', expense: '5814.00', net: '-464.00' },
  { start: '2026-04-01', end: '2026-04-30', expense: '4120.00', net: '1230.00' },
  { start: '2026-05-01', end: '2026-05-31', expense: '4156.00', net: '1194.00' },
  { start: '2026-06-01', end: '2026-06-30', expense: '4180.00', net: '1170.00' },
  { start: '2026-07-01', end: '2026-07-31', expense: '4030.00', net: '1320.00' },
]

// window.today is 2026-07-20, so July 2026 (the last bucket) is the in-progress month
// and complete:false; runway.until is today + round(2.836 × 30.44) = 86 days.
const BUCKETS: BucketWire[] = MONTHS.map((m, i) => ({
  start_date: m.start,
  end_date: m.end,
  complete: i !== MONTHS.length - 1,
  income: '5350.00',
  expense: m.expense,
  transfers_in: '250.00',
  transfers_out: '260.00',
  net: m.net,
  // December has no top category at all; March's biggest spend is Uncategorised.
  top_expense_category:
    i === 4 ? null : i === 7 ? { category_id: null, amount: '1204.00' } : { category_id: 'housing', amount: '1825.00' },
}))

const PRIOR_TOTALS: NonNullable<CashflowWire['prior']>['totals'] = {
  income: '61000.00',
  expense: '50100.00',
  transfers_in: '2850.00',
  transfers_out: '2900.00',
  net: '10900.00',
}

/*
 * The top-level figures both bodies share, as constants of their own. The free
 * body used to read them off CASHFLOW_FIXTURE (`CASHFLOW_FIXTURE.totals`), and
 * a module-scope property read is one a bundler cannot prove free of getters,
 * so it kept the whole premium fixture in a production build behind the probe.
 */
const WINDOW: CashflowWire['window'] = { start_date: '2025-08-01', end_date: '2026-07-31', granularity: 'month', today: '2026-07-20' }

const TOTALS: CashflowWire['totals'] = {
  income: '64230.00',
  expense: '52800.00',
  transfers_in: '3050.00',
  transfers_out: '3120.00',
  net: '11430.00',
  average_per_bucket: {
    income: '5352.50',
    expense: '4400.00',
    transfers_in: '254.17',
    transfers_out: '260.00',
    net: '952.50',
  },
  complete_bucket_count: 11,
  deficit_bucket_count: 2,
  deficit_buckets: [
    { start_date: '2025-12-01', end_date: '2025-12-31' },
    { start_date: '2026-03-01', end_date: '2026-03-31' },
  ],
}

const SCOPE: CashflowWire['scope'] = { account_count: 5, shared_account_count: 2 }

const AVAILABLE: CashflowWire['available'] = { earliest_transaction_date: '2024-02-14' }

const FX: CashflowWire['fx'] = {
  source: 'bank_of_canada',
  rates_as_of: '2026-10-09',
  unconverted: [{ currency: 'EUR', transaction_count: 3, amount: '120.00' }],
}

/** Premium body: every include section present. */
export const CASHFLOW_FIXTURE: CashflowWire = {
  currency: 'CAD',
  window: WINDOW,
  totals: TOTALS,
  prior: {
    start_date: '2024-08-01',
    end_date: '2025-07-31',
    totals: PRIOR_TOTALS,
    categories: [
      { category_id: 'housing', amount: '20400.00' },
      { category_id: 'groceries', amount: '8100.00' },
      { category_id: 'dining', amount: '3900.00' },
      { category_id: 'health', amount: '2500.00' },
      { category_id: 'subscriptions', amount: '2400.00' },
    ],
    groups: [
      { group_id: 'p-living', amount: '36000.00' },
      { group_id: 'p-lifestyle', amount: '7500.00' },
      { group_id: 'health', amount: '2500.00' },
      { group_id: null, amount: '6600.00' },
    ],
  },
  scope: SCOPE,
  available: AVAILABLE,
  fx: FX,
  buckets: BUCKETS,
  categories: {
    items: [
      { category_id: 'housing', group_id: 'p-living', amount: '21900.00', share_pct: '41.0', series: series('1825.00') },
      { category_id: 'groceries', group_id: 'p-living', amount: '8640.00', share_pct: '16', series: series('720.00') },
      // No prior row for transport: its priorCents must come out null, not 0.
      { category_id: 'transport', group_id: 'p-living', amount: '5580.00', share_pct: '11.0', series: series('465.00') },
      { category_id: 'dining', group_id: 'p-lifestyle', amount: '3810.00', share_pct: '7.0', series: series('320.00') },
      { category_id: 'health', group_id: 'health', amount: '2780.00', share_pct: '5.0', series: series('230.00') },
      { category_id: 'subscriptions', group_id: 'p-lifestyle', amount: '2540.00', share_pct: '5.0', series: series('210.00') },
    ],
    uncategorised: { transaction_count: 184, amount: '6030.00' },
  },
  // 37,000 + 6,990 + 2,780 + 6,030 = 52,800, the window's expense; the null group is the uncategorised 6,030.
  // Shares sum to exactly 100, as the backend guarantees (largest remainder:
  // 70.08 / 13.24 / 5.27 / 11.42 floor to 99, and the largest fraction rounds up).
  groups: [
    { group_id: 'p-living', amount: '37000.00', share_pct: '70.0' },
    { group_id: 'p-lifestyle', amount: '6990.00', share_pct: '13' },
    { group_id: 'health', amount: '2780.00', share_pct: '5.0' },
    { group_id: null, amount: '6030.00', share_pct: '12.0' },
  ],
  merchants: {
    items: [
      { merchant_id: 'm-landlord', name: 'Maple Property Mgmt', category_id: 'housing', amount: '12000.00', transaction_count: 12, is_recurring: true },
      { merchant_id: 'm-grocer', name: 'Fresh Mart', category_id: 'groceries', amount: '9000.00', transaction_count: 118, is_recurring: false },
      { merchant_id: 'm-transit', name: 'City Transit', category_id: 'transport', amount: '6409.00', transaction_count: 40, is_recurring: false },
      { merchant_id: 'm-stream', name: 'StreamBox', category_id: 'subscriptions', amount: '5000.00', transaction_count: 12, is_recurring: true },
      { merchant_id: 'm-cafe', name: 'Corner Cafe', category_id: null, amount: '4000.00', transaction_count: 88, is_recurring: false },
      { merchant_id: 'm-hydro', name: 'Lakeside Hydro', category_id: 'housing', amount: '1584.00', transaction_count: 13, is_recurring: true },
    ],
    merchant_count: 86,
    // DELIBERATELY inconsistent: expense 52,800 − the top six (37,993) is 14,807,
    // but the server's remainder is 16,391 (the render's figure). The row must
    // show the server's amount, so a client-side subtraction goes red.
    remainder: { merchant_count: 80, transaction_count: 410, amount: '16391.00' },
    // Backend 512's halves of that remainder: 15,000 + 1,391 = 16,391, 380 + 30 = 410.
    other: { merchant_count: 80, transaction_count: 380, amount: '15000.00' },
    unassigned: { transaction_count: 30, amount: '1391.00' },
  },
  income_sources: [
    { merchant_id: 'm-employer', name: 'Northwind Ltd', category_id: 'salary', amount: '52000.00', transaction_count: 24 },
    { merchant_id: 'm-freelance', name: 'Freelance clients', category_id: 'salary', amount: '8230.00', transaction_count: 9 },
    { merchant_id: null, name: 'Interest', category_id: null, amount: '3000.00', transaction_count: 12 },
    { merchant_id: 'm-refund', name: 'Tax refund', category_id: null, amount: '1000.00', transaction_count: 1 },
  ],
  runway: {
    cash_balance: '12480.00',
    average_monthly_expense: '4400.00',
    months_of_history: 24,
    months: '2.8',
    until: '2026-10-14',
  },
}

/** Same top-level figures, none of the include sections. */
export const CASHFLOW_FREE_FIXTURE: CashflowWire = {
  currency: 'CAD',
  window: WINDOW,
  totals: TOTALS,
  prior: {
    start_date: '2024-08-01',
    end_date: '2025-07-31',
    totals: PRIOR_TOTALS,
  },
  scope: SCOPE,
  available: AVAILABLE,
  fx: FX,
}

/** Three weeks of history: no prior window, no runway. */
export const CASHFLOW_NEW_ACCOUNT_FIXTURE: CashflowWire = {
  currency: 'CAD',
  window: { start_date: '2026-09-22', end_date: '2026-10-12', granularity: 'month', today: '2026-10-12' },
  totals: {
    income: '2400.00',
    expense: '1350.00',
    transfers_in: '0.00',
    transfers_out: '0.00',
    net: '1050.00',
    average_per_bucket: {
      income: '800.00',
      expense: '450.00',
      transfers_in: '0.00',
      transfers_out: '0.00',
      net: '350.00',
    },
    complete_bucket_count: 0,
    deficit_bucket_count: 0,
    deficit_buckets: [],
  },
  prior: null,
  scope: { account_count: 1, shared_account_count: 0 },
  available: { earliest_transaction_date: '2026-09-22' },
  fx: { source: 'bank_of_canada', rates_as_of: null, unconverted: [] },
  buckets: [
    { start_date: '2026-09-21', end_date: '2026-09-27', complete: false, income: '2400.00', expense: '400.00', transfers_in: '0.00', transfers_out: '0.00', net: '2000.00', top_expense_category: { category_id: 'housing', amount: '300.00' } },
    { start_date: '2026-09-28', end_date: '2026-10-04', complete: false, income: '0.00', expense: '500.00', transfers_in: '0.00', transfers_out: '0.00', net: '-500.00', top_expense_category: { category_id: 'groceries', amount: '200.00' } },
    { start_date: '2026-10-05', end_date: '2026-10-11', complete: false, income: '0.00', expense: '450.00', transfers_in: '0.00', transfers_out: '0.00', net: '-450.00', top_expense_category: null },
  ],
  categories: {
    items: [
      { category_id: 'housing', group_id: 'housing', amount: '700.00', share_pct: '51.9', series: ['300.00', '200.00', '200.00'] },
    ],
    uncategorised: { transaction_count: 3, amount: '650.00' },
  },
  groups: [
    { group_id: 'housing', amount: '700.00', share_pct: '51.9' },
    { group_id: null, amount: '650.00', share_pct: '48.1' },
  ],
  merchants: {
    items: [],
    merchant_count: 0,
    remainder: { merchant_count: 0, transaction_count: 0, amount: '0.00' },
    other: { merchant_count: 0, transaction_count: 0, amount: '0.00' },
    unassigned: { transaction_count: 0, amount: '0.00' },
  },
  income_sources: [{ merchant_id: null, name: 'Employer', category_id: null, amount: '2400.00', transaction_count: 1 }],
  runway: { cash_balance: '900.00', average_monthly_expense: '1350.00', months_of_history: 0, months: null, until: null },
}

/**
 * The category map the screen takes as a prop (P4), for the fixtures' ids, with
 * the render's human names. A function, called inside the probe component, so
 * a production build keeps none of it. No colours: the probe draws none.
 */
export function cashflowCategoriesFixture(): Map<string, CategoryInfo> {
  const entries: [string, string, string | null][] = [
    ['p-living', 'Essentials', null],
    ['p-lifestyle', 'Lifestyle', null],
    ['housing', 'Housing', 'p-living'],
    ['groceries', 'Groceries', 'p-living'],
    ['transport', 'Transport', 'p-living'],
    ['health', 'Health', null],
    ['dining', 'Dining', 'p-lifestyle'],
    ['subscriptions', 'Subscriptions', 'p-lifestyle'],
    ['salary', 'Salary', null],
    // The period-focus probe's extra ids.
    ['shopping', 'Shopping', 'p-lifestyle'],
    ['travel', 'Travel', 'p-lifestyle'],
    ['india-transferred', 'India Transferred', null],
  ]
  return new Map(entries.map(([id, name, parentId]) => [id, { name, parentId }]))
}

/*
 * The probe's bodies for the period-focus screen. Functions, so a production
 * build that never calls them keeps none of the data; each builds its own
 * constants and reads nothing off another fixture. `toCashflow` accepts every
 * one as it stands (they are wire JSON).
 *
 * Today is 2026-10-13, so October is partial and its prior window is the same
 * span of September (1–12 Sep), as the backend shapes it.
 */
const PROBE_TODAY = '2026-10-13'

const PROBE_SCOPE: CashflowWire['scope'] = { account_count: 5, shared_account_count: 2 }
const PROBE_AVAILABLE: CashflowWire['available'] = { earliest_transaction_date: '2024-11-04' }
const PROBE_FX: CashflowWire['fx'] = { source: 'bank_of_canada', rates_as_of: '2026-10-12', unconverted: [] }

/** The October-to-date totals both period bodies carry. */
function probePeriodHead(): Pick<CashflowWire, 'currency' | 'window' | 'totals' | 'prior' | 'scope' | 'available' | 'fx'> {
  const totals = { income: '6200.00', expense: '3981.50', transfers_in: '250.00', transfers_out: '260.00', net: '2218.50' }
  return {
    currency: 'CAD',
    window: { start_date: '2026-10-01', end_date: '2026-10-31', granularity: 'month', today: PROBE_TODAY },
    totals: {
      ...totals,
      average_per_bucket: totals,
      complete_bucket_count: 0,
      deficit_bucket_count: 0,
      deficit_buckets: [],
    },
    prior: {
      start_date: '2026-09-01',
      end_date: '2026-09-12',
      totals: { income: '5900.00', expense: '3400.00', transfers_in: '200.00', transfers_out: '210.00', net: '2500.00' },
    },
    scope: PROBE_SCOPE,
    available: PROBE_AVAILABLE,
    fx: PROBE_FX,
  }
}

/** Free: the totals and the prior's totals, no sections. */
export function cashflowFreePeriodProbeFixture(): CashflowWire {
  return probePeriodHead()
}

/**
 * Premium period: October 2026 so far, nine categories (one net negative, money
 * back: "India Transferred"), groups, eight merchants, five income sources and
 * a runway of 2.8 months.
 */
export function cashflowPeriodProbeFixture(): CashflowWire {
  const head = probePeriodHead()
  // Thirteen days of October, one point a day, flat: the probe draws the shape, not the figures.
  const days = (point: string): string[] => Array.from({ length: 13 }, () => point)
  return {
    ...head,
    prior: {
      ...(head.prior as NonNullable<CashflowWire['prior']>),
      categories: [
        { category_id: 'housing', amount: '1825.00' },
        { category_id: 'groceries', amount: '700.00' },
        { category_id: 'dining', amount: '380.00' },
        { category_id: 'health', amount: '150.00' },
      ],
      groups: [
        { group_id: 'p-living', amount: '2600.00' },
        { group_id: 'p-lifestyle', amount: '700.00' },
        { group_id: 'health', amount: '150.00' },
        { group_id: null, amount: '150.00' },
      ],
    },
    // Nine rows. Transport has no prior row, so its prior must come out null, not 0.
    categories: {
      items: [
        { category_id: 'housing', group_id: 'p-living', amount: '1825.00', share_pct: '40.9', series: days('140.38') },
        { category_id: 'groceries', group_id: 'p-living', amount: '780.50', share_pct: '17.5', series: days('60.04') },
        { category_id: 'transport', group_id: 'p-living', amount: '310.00', share_pct: '6.9', series: days('23.85') },
        { category_id: 'dining', group_id: 'p-lifestyle', amount: '420.00', share_pct: '9.4', series: days('32.31') },
        { category_id: 'health', group_id: 'health', amount: '210.00', share_pct: '4.7', series: days('16.15') },
        { category_id: 'subscriptions', group_id: 'p-lifestyle', amount: '96.00', share_pct: '2.2', series: days('7.38') },
        { category_id: 'shopping', group_id: 'p-lifestyle', amount: '340.00', share_pct: '7.6', series: days('26.15') },
        { category_id: 'travel', group_id: 'p-lifestyle', amount: '290.00', share_pct: '6.5', series: days('22.31') },
        // Net negative: refunds exceed spend, so it is money back, and its share reads 0.
        { category_id: 'india-transferred', group_id: 'india-transferred', amount: '-480.00', share_pct: '0', series: days('-36.92') },
      ],
      uncategorised: { transaction_count: 11, amount: '190.00' },
    },
    // 2,915.50 + 1,146.00 + 210.00 − 480.00 + 190.00 = 3,981.50, the period's expense.
    groups: [
      { group_id: 'p-living', amount: '2915.50', share_pct: '65.3' },
      { group_id: 'p-lifestyle', amount: '1146.00', share_pct: '25.7' },
      { group_id: 'health', amount: '210.00', share_pct: '4.7' },
      { group_id: 'india-transferred', amount: '-480.00', share_pct: '0' },
      { group_id: null, amount: '190.00', share_pct: '4.3' },
    ],
    merchants: {
      items: [
        { merchant_id: 'm-landlord', name: 'Maple Property Mgmt', category_id: 'housing', amount: '1800.00', transaction_count: 1, is_recurring: true },
        { merchant_id: 'm-grocer', name: 'Fresh Mart', category_id: 'groceries', amount: '640.00', transaction_count: 9, is_recurring: false },
        { merchant_id: 'm-air', name: 'Sunrise Air', category_id: 'travel', amount: '290.00', transaction_count: 1, is_recurring: false },
        { merchant_id: 'm-transit', name: 'City Transit', category_id: 'transport', amount: '210.00', transaction_count: 6, is_recurring: false },
        { merchant_id: 'm-cafe', name: 'Corner Cafe', category_id: 'dining', amount: '188.00', transaction_count: 14, is_recurring: false },
        { merchant_id: 'm-hydro', name: 'Lakeside Hydro', category_id: 'housing', amount: '125.00', transaction_count: 1, is_recurring: true },
        { merchant_id: 'm-pharmacy', name: 'Corner Pharmacy', category_id: 'health', amount: '98.00', transaction_count: 3, is_recurring: false },
        { merchant_id: 'm-stream', name: 'StreamBox', category_id: 'subscriptions', amount: '96.00', transaction_count: 1, is_recurring: true },
      ],
      merchant_count: 63,
      // Expense 3,981.50 less the top eight (3,447.00).
      remainder: { merchant_count: 55, transaction_count: 61, amount: '534.50' },
      // Backend 512's halves of that remainder: 412.25 + 122.25 = 534.50, 49 + 12 = 61.
      other: { merchant_count: 55, transaction_count: 49, amount: '412.25' },
      unassigned: { transaction_count: 12, amount: '122.25' },
    },
    income_sources: [
      { merchant_id: 'm-employer', name: 'Northwind Ltd', category_id: 'salary', amount: '4800.00', transaction_count: 2 },
      { merchant_id: 'm-freelance', name: 'Freelance clients', category_id: 'salary', amount: '900.00', transaction_count: 2 },
      { merchant_id: null, name: 'Interest', category_id: null, amount: '300.00', transaction_count: 1 },
      { merchant_id: 'm-refund', name: 'Tax refund', category_id: null, amount: '150.00', transaction_count: 1 },
      { merchant_id: 'm-resale', name: 'Marketplace sales', category_id: null, amount: '50.00', transaction_count: 3 },
    ],
    runway: { cash_balance: '12480.00', average_monthly_expense: '4400.00', months_of_history: 24, months: '2.8', until: '2027-01-06' },
  }
}

/**
 * The history call: 24 monthly buckets, November 2024 to October 2026, the last
 * still open, and three deficit months (Dec 2024, Jan 2026, Apr 2026).
 */
export function cashflowHistoryProbeFixture(): CashflowWire {
  // [start, end, complete, income, expense, net], November 2024 to October 2026; October is to date.
  const rows: [string, string, boolean, string, string, string][] = [
    ['2024-11-01', '2024-11-30', true, '5350.00', '4100.00', '1250.00'],
    ['2024-12-01', '2024-12-31', true, '5350.00', '5850.00', '-500.00'],
    ['2025-01-01', '2025-01-31', true, '5350.00', '4050.00', '1300.00'],
    ['2025-02-01', '2025-02-28', true, '5350.00', '3980.00', '1370.00'],
    ['2025-03-01', '2025-03-31', true, '5350.00', '4120.00', '1230.00'],
    ['2025-04-01', '2025-04-30', true, '5350.00', '4150.00', '1200.00'],
    ['2025-05-01', '2025-05-31', true, '5350.00', '4200.00', '1150.00'],
    ['2025-06-01', '2025-06-30', true, '5350.00', '4180.00', '1170.00'],
    ['2025-07-01', '2025-07-31', true, '5350.00', '4220.00', '1130.00'],
    ['2025-08-01', '2025-08-31', true, '5350.00', '4250.00', '1100.00'],
    ['2025-09-01', '2025-09-30', true, '5350.00', '4300.00', '1050.00'],
    ['2025-10-01', '2025-10-31', true, '5350.00', '4280.00', '1070.00'],
    ['2025-11-01', '2025-11-30', true, '5350.00', '4310.00', '1040.00'],
    ['2025-12-01', '2025-12-31', true, '5350.00', '4340.00', '1010.00'],
    ['2026-01-01', '2026-01-31', true, '5350.00', '5700.00', '-350.00'],
    ['2026-02-01', '2026-02-28', true, '5350.00', '4050.00', '1300.00'],
    ['2026-03-01', '2026-03-31', true, '5350.00', '3990.00', '1360.00'],
    ['2026-04-01', '2026-04-30', true, '5350.00', '5814.00', '-464.00'],
    ['2026-05-01', '2026-05-31', true, '5350.00', '4120.00', '1230.00'],
    ['2026-06-01', '2026-06-30', true, '5350.00', '4156.00', '1194.00'],
    ['2026-07-01', '2026-07-31', true, '5350.00', '4180.00', '1170.00'],
    ['2026-08-01', '2026-08-31', true, '5350.00', '4030.00', '1320.00'],
    ['2026-09-01', '2026-09-30', true, '5350.00', '4210.00', '1140.00'],
    ['2026-10-01', '2026-10-31', false, '6200.00', '3981.50', '2218.50'],
  ]
  const buckets: BucketWire[] = rows.map(([start, end, complete, income, expense, net]) => ({
    start_date: start,
    end_date: end,
    complete,
    income,
    expense,
    transfers_in: '250.00',
    transfers_out: '260.00',
    net,
    top_expense_category: { category_id: 'housing', amount: '1825.00' },
  }))
  const head = probePeriodHead()
  const deficits = buckets.filter((b) => b.net.startsWith('-'))
  return {
    ...head,
    window: { start_date: '2024-11-01', end_date: '2026-10-31', granularity: 'month', today: PROBE_TODAY },
    prior: null,
    totals: {
      ...head.totals,
      complete_bucket_count: 23,
      deficit_bucket_count: deficits.length,
      deficit_buckets: deficits.map((b) => ({ start_date: b.start_date, end_date: b.end_date })),
    },
    buckets,
  }
}
